import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import EmploymentStatusForm, {
  EmploymentStatusFormRef,
  EmploymentStatusValues,
} from './components/EmploymentStatusForm';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Icons } from '@nxsys/types/icons';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { toTotalAttachmentPayload } from '@nxsys/constants/AppConstants';
import clsx from 'clsx';
import { Option } from '@nxsys/types/models/Common';
import ContractTypeForm, {
  ContractTypeFormRef,
  ContractTypeValues,
} from './components/ContractTypeForm';
import ContractDetailForm, {
  ContractDetailFormRef,
  ContractDetailValues,
} from './components/ContractDetailForm';
import PersonalDetailForm, {
  PersonalDetailFormRef,
  PersonalDetailValues,
} from './components/PersonalDetailForm';
import BankDetailForm, {
  BankDetailFormRef,
  BankDetailValues,
} from './components/BankDetailForm';
import EmploymentForm, {
  EmploymentFormRef,
  EmploymentValues,
} from './components/EmploymentForm';
import PensionForm, {
  PensionFormRef,
  PensionValues,
} from './components/PensionForm';
import AttachmentOrderForm, {
  AttachmentOrderFormRef,
  AttachmentOrderValues,
} from './components/AttachmentOrderForm';
import { showMessage } from '@nxsys/utils/message';
import { CreateEmployeeParams } from '@nxsys/types/api';
import {
  PAYMENT_MODEL_ENUM,
} from '@nxsys/constants/AppEnums';
import { useCreateEmployee, useUpdateBankByEmployeeId } from '@nxsys/hooks/api';
import { buildWorkingPatternCreateParams } from '../components/workingPatternUtils';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';

type CreateEmployeeModalProps = {
  isOpen: boolean;
  toggle: () => void;
  refetchList?: () => void;
};

enum CREATE_EMPLOYEE_STEP {
  EMPLOYMENT_STATUS = 'EMPLOYMENT_STATUS',
  CONTRACT_TYPE = 'CONTRACT_TYPE',
  CONTRACT_DETAIL = 'CONTRACT_DETAIL',
  PERSONAL_DETAIL = 'PERSONAL_DETAIL',
  BANK_DETAIL = 'BANK_DETAIL',
  EMPLOYMENT = 'EMPLOYMENT',
  PENSION = 'PENSION',
  ATTACHMENT_ORDER = 'ATTACHMENT_ORDER',
}

type FormValues = EmploymentStatusValues &
  ContractTypeValues &
  ContractDetailValues &
  PersonalDetailValues &
  BankDetailValues &
  EmploymentValues &
  PensionValues &
  AttachmentOrderValues;

const CreateEmployeeModal: React.FC<CreateEmployeeModalProps> = ({
  isOpen,
  toggle,
  refetchList = () => {},
}) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  // Country-only: `isBureau &&` meant an umbrella-Ireland tenant always
  // matched the `!isBureau` branch below (UK-shaped payload + wrong step
  // list showing Pension/Attachment Order) and never reached the Ireland
  // payload branch.
  const isIreland = payrollCountry === IRELAND;

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formValues, setFormValues] = useState<FormValues | null>(null);

  const isCIS =
    formValues?.paymentMethod?.value === PAYMENT_MODEL_ENUM.CIS ||
    formValues?.paymentMethod?.value === PAYMENT_MODEL_ENUM.NON_CIS;

  const steps = useMemo(
    () => [
      {
        label: messages['common.employmentStatus'],
        value: CREATE_EMPLOYEE_STEP.EMPLOYMENT_STATUS,
      },
      {
        label: messages['common.contractType'],
        value: CREATE_EMPLOYEE_STEP.CONTRACT_TYPE,
      },
      {
        label: messages['common.contractDetail'],
        value: CREATE_EMPLOYEE_STEP.CONTRACT_DETAIL,
      },
      {
        label: messages['common.personalDetail'],
        value: CREATE_EMPLOYEE_STEP.PERSONAL_DETAIL,
      },
      {
        label: messages['common.bankDetails'] ?? 'Bank Details',
        value: CREATE_EMPLOYEE_STEP.BANK_DETAIL,
      },
      {
        label: messages['common.employment'],
        value: CREATE_EMPLOYEE_STEP.EMPLOYMENT,
      },
      ...(!isIreland && !isCIS
        ? [
            {
              label: messages['common.pension'],
              value: CREATE_EMPLOYEE_STEP.PENSION,
            },
          ]
        : []),
      ...(!isCIS && !isIreland
        ? [
            {
              label: messages['common.attachmentOrder'],
              value: CREATE_EMPLOYEE_STEP.ATTACHMENT_ORDER,
            },
          ]
        : []),
    ],
    [messages, isIreland, isCIS],
  );

  // Clamp step index when steps array shrinks (e.g. CIS hides Pension/Attachment Order)
  const activeStepIndex = Math.min(currentStepIndex, steps.length - 1);

  const employmentStatusFormRef = useRef<EmploymentStatusFormRef>(null);
  const contractTypeFormRef = useRef<ContractTypeFormRef>(null);
  const contractDetailFormRef = useRef<ContractDetailFormRef>(null);
  const personalDetailFormRef = useRef<PersonalDetailFormRef>(null);
  const bankDetailFormRef = useRef<BankDetailFormRef>(null);
  const employmentFormRef = useRef<EmploymentFormRef>(null);
  const pensionFormRef = useRef<PensionFormRef>(null);
  const attachmentOrderFormRef = useRef<AttachmentOrderFormRef>(null);

  const { run: createEmployee, loading: createEmployeeLoading } =
    useCreateEmployee();
  // The create endpoint takes no bank payload, so bank details are saved right
  // after the employee exists, using the same endpoint as the Bank Details tab.
  const { run: updateBank, loading: updateBankLoading } =
    useUpdateBankByEmployeeId();

  const renderStep = useCallback(
    (step: Option<string>, index: number) => {
      const isActive = index === activeStepIndex;
      const isCompleted = index < activeStepIndex;

      return (
        <div
          key={step.value}
          className={clsx(
            'flex justify-between items-center rounded-[8px] p-[12px] border',
            {
              'border-primary-600': isActive || isCompleted,
              'border-gray-300 bg-gray-50': !isActive && !isCompleted,
              'bg-primary-50': isCompleted,
            },
          )}
        >
          <div className="flex items-center gap-x-[8px]">
            {isCompleted ? (
              <Icons.IconCheckCircleFilled
                color={defaultTheme.theme.palette.brand}
              />
            ) : (
              <div
                className={clsx(
                  'w-[20px] h-[20px] rounded-full border  flex items-center justify-center text-[12px] font-medium',
                  {
                    'border-primary-600 text-primary-600': isActive,
                    'border-black text-black': !isActive,
                  },
                )}
              >
                {index + 1}
              </div>
            )}
            <div
              className={clsx('text-[16px]', {
                'text-primary-600': isActive || isCompleted,
              })}
            >
              {step.label}
            </div>
          </div>
          {isActive && (
            <Icons.IconArrowRight color={defaultTheme.theme.palette.brand} />
          )}
        </div>
      );
    },
    [activeStepIndex],
  );

  const renderFormByStep = useCallback(
    (step: Option<string>) => {
      switch (step.value) {
        case CREATE_EMPLOYEE_STEP.EMPLOYMENT_STATUS:
          return (
            <EmploymentStatusForm
              formRef={employmentStatusFormRef}
              initialValues={formValues as EmploymentStatusValues}
            />
          );
        case CREATE_EMPLOYEE_STEP.CONTRACT_TYPE:
          return (
            <ContractTypeForm
              formRef={contractTypeFormRef}
              initialValues={formValues as ContractTypeValues}
            />
          );
        case CREATE_EMPLOYEE_STEP.CONTRACT_DETAIL:
          return (
            <ContractDetailForm
              formRef={contractDetailFormRef}
              initialValues={formValues as ContractDetailValues}
            />
          );
        case CREATE_EMPLOYEE_STEP.PERSONAL_DETAIL:
          return (
            <PersonalDetailForm
              formRef={personalDetailFormRef}
              initialValues={formValues as PersonalDetailValues}
            />
          );
        case CREATE_EMPLOYEE_STEP.BANK_DETAIL:
          return (
            <BankDetailForm
              formRef={bankDetailFormRef}
              initialValues={formValues as BankDetailValues}
            />
          );
        case CREATE_EMPLOYEE_STEP.EMPLOYMENT:
          return (
            <EmploymentForm
              formRef={employmentFormRef}
              initialValues={formValues as EmploymentValues}
              contractDetailType={formValues?.contractDetailType || ''}
              employmentStatus={formValues?.employmentStatus || ''}
            />
          );
        case CREATE_EMPLOYEE_STEP.PENSION:
          return (
            <PensionForm
              formRef={pensionFormRef}
              initialValues={formValues as PensionValues}
              employer={formValues?.employer?.fullData}
            />
          );
        case CREATE_EMPLOYEE_STEP.ATTACHMENT_ORDER:
          return (
            <AttachmentOrderForm
              formRef={attachmentOrderFormRef}
              initialValues={formValues as AttachmentOrderValues}
            />
          );
        default:
          return null;
      }
    },
    [formValues],
  );

  const validateBeforeChangeStep = async (stepValue: string) => {
    if (stepValue === CREATE_EMPLOYEE_STEP.EMPLOYMENT_STATUS) {
      return await employmentStatusFormRef.current?.isValidForm();
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.CONTRACT_TYPE) {
      return await contractTypeFormRef.current?.isValidForm();
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.CONTRACT_DETAIL) {
      return await contractDetailFormRef.current?.isValidForm();
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.PERSONAL_DETAIL) {
      return await personalDetailFormRef.current?.isValidForm();
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.BANK_DETAIL) {
      return await bankDetailFormRef.current?.isValidForm();
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.EMPLOYMENT) {
      return await employmentFormRef.current?.isValidForm();
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.PENSION) {
      return await pensionFormRef.current?.isValidForm();
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.ATTACHMENT_ORDER) {
      return await attachmentOrderFormRef.current?.isValidForm();
    }
    return true;
  };

  const getValuesByStep = async (stepValue: string): Promise<FormValues> => {
    let values = {} as FormValues;

    if (stepValue === CREATE_EMPLOYEE_STEP.EMPLOYMENT_STATUS) {
      values =
        (await employmentStatusFormRef.current?.getValues()) as FormValues;
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.CONTRACT_TYPE) {
      values = (await contractTypeFormRef.current?.getValues()) as FormValues;
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.CONTRACT_DETAIL) {
      values = (await contractDetailFormRef.current?.getValues()) as FormValues;
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.PERSONAL_DETAIL) {
      values = (await personalDetailFormRef.current?.getValues()) as FormValues;
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.BANK_DETAIL) {
      values = (await bankDetailFormRef.current?.getValues()) as FormValues;
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.EMPLOYMENT) {
      values = (await employmentFormRef.current?.getValues()) as FormValues;
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.PENSION) {
      values = (await pensionFormRef.current?.getValues()) as FormValues;
    }
    if (stepValue === CREATE_EMPLOYEE_STEP.ATTACHMENT_ORDER) {
      values =
        (await attachmentOrderFormRef.current?.getValues()) as FormValues;
    }

    return values;
  };

  const handleBack = async () => {
    const values = await getValuesByStep(steps[activeStepIndex].value);
    setFormValues({
      ...formValues,
      ...values,
    });
    setCurrentStepIndex(activeStepIndex - 1);
  };

  const handleSubmitToContinue = async () => {
    const isValid = await validateBeforeChangeStep(
      steps[activeStepIndex].value,
    );
    if (!isValid) return;

    const values = await getValuesByStep(steps[activeStepIndex].value);
    setFormValues({
      ...formValues,
      ...values,
    });

    setCurrentStepIndex(activeStepIndex + 1);
  };

  const handleSubmit = async () => {
    const isValid = await validateBeforeChangeStep(
      steps[activeStepIndex].value,
    );
    if (!isValid) return;
    const lastStepValues = await getValuesByStep(steps[activeStepIndex].value);
    const allValues = { ...formValues, ...lastStepValues };

    let payload: CreateEmployeeParams = {} as CreateEmployeeParams;

    if (!isBureau && !isIreland) {
      payload = {
        // Step 1:
        workerType: allValues?.employmentStatus || null,
        // Step 2:
        contractType: allValues?.contractType || null,
        // Step 3:
        contractDetail: allValues?.contractDetailType || null,
        dateOfJoining:
          allValues?.employmentStartDate?.format('YYYY-MM-DD') || null,
        // Step 4:
        title: allValues?.title?.value || null,
        forename: allValues?.forename || '',
        surname: allValues?.surname || '',
        gender: allValues?.gender?.value || null,
        DOB: allValues?.DOB?.format('YYYY-MM-DD') || null,
        email: allValues?.email || '',
        mobile: allValues?.phoneNumber || '',
        NINumber: allValues?.NINumber || '',
        address: [allValues?.address1 || '', allValues?.address2 || ''],
        city: allValues?.city || '',
        county:
          typeof allValues?.county === 'object'
            ? (allValues?.county as any)?.value || ''
            : allValues?.county || '',
        postCode: allValues?.postCode || '',
        country: allValues?.country?.value || null,
        // Step 6:
        department: allValues?.department?.value || '',
        employmentSetting: {
          companyId: allValues?.employer?.value || '',
          employmentStartDate:
            allValues?.employmentStartDate?.format('YYYY-MM-DD') || null,
          employmentBasis: allValues?.employmentBasis?.value || null,
          payFrequency: allValues?.payFrequency || null,
          paymentMethod: allValues?.paymentMethod?.value || null,
          ...(allValues?.paymentMethod?.value === PAYMENT_MODEL_ENUM.CIS ||
          allValues?.paymentMethod?.value === PAYMENT_MODEL_ENUM.NON_CIS
            ? {
                payeUmbrellaSettings: null,
                cisSettings: {
                  businessType: allValues?.businessType?.value || null,
                  UTRNo: allValues?.utrNumber || '',
                  VATRegNo: allValues?.vatRegNo || '',
                  domesticReverseChargeApplicable:
                    allValues?.domesticReverseChargeApplicable || false,
                  cisStatus: allValues?.cisStatus?.value || null,
                  personalVerificationNumber:
                    allValues?.personalVerificationNumber || '',
                  verificationDate:
                    allValues?.verificationDate?.format('YYYY-MM-DD') || null,
                  matchDate: allValues?.matchDate?.format('YYYY-MM-DD') || null,
                  companyName: allValues?.companyName || '',
                  registrationNumber: allValues?.registrationNumber || '',
                  firmName: allValues?.firmName || '',
                  partnersName: allValues?.partnersName || '',
                  partnersCategory: allValues?.partnersCategory?.value || null,
                },
              }
            : {
                payeUmbrellaSettings: {
                  taxCode: allValues?.taxCode || '',
                  NICategory: allValues?.NICategory?.value || null,
                  starterDeclaration:
                    allValues?.starterDeclaration?.value || null,
                  taxBasis: allValues?.taxBasis?.value || null,
                  NIDirectorStartDate:
                    allValues?.NIDirectorStartDate?.format('YYYY-MM-DD') ||
                    null,
                  NIDirectorMethod: allValues?.NIDirectorMethod?.value || null,
                  workplacePostcode: allValues?.workplacePostcode || '',
                  previousPayrollId: allValues?.previousPayrollId || '',
                  previousPayrollIdUnknown:
                    allValues?.previousPayrollIdUnknown ?? false,
                  salary: Number(allValues?.salary) || 0,
                  previousJobTaxInfo: {
                    p45GrossToDate: Number(allValues?.p45GrossToDate) || 0,
                    p45TaxToDate: Number(allValues?.p45TaxToDate) || 0,
                  },
                  openingBalance: {
                    eeNI: Number(allValues?.employeeNI) || 0,
                    erNI: Number(allValues?.employerNI) || 0,
                    eePension: Number(allValues?.employeePension) || 0,
                    erPension: Number(allValues?.employerPension) || 0,
                    stLoan: Number(allValues?.studentLoan) || 0,
                    pgLoan: Number(allValues?.postGraduateStudentLoan) || 0,
                    smp: Number(allValues?.smp) || 0,
                    spp: Number(allValues?.spp) || 0,
                    sap: Number(allValues?.sap) || 0,
                    ssp: Number(allValues?.ssp) || 0,
                    sppa: Number(allValues?.shpp) || 0,
                    sncp: Number(allValues?.sncp) || 0,
                    grossToLel: Number(allValues?.grossToLel) || 0,
                    grossLelToPt: Number(allValues?.grossLelToPt) || 0,
                    grossPtToUEL: Number(allValues?.grossPtToUEL) || 0,
                    gross: Number(allValues?.gross) || 0,
                    grossForTax: Number(allValues?.grossForTax) || 0,
                    grossForNI: Number(allValues?.grossForNI) || 0,
                    tax: Number(allValues?.tax) || 0,
                    // Director NI Contribution
                    ...(allValues?.employmentStatus === 'DIRECTOR' && {
                      grossToLelAsDirector: Number(allValues?.grossToLelAsDirector) || 0,
                      grossLelToPtAsDirector: Number(allValues?.grossLelToPtAsDirector) || 0,
                      grossPtToUELAsDirector: Number(allValues?.grossPtToUELAsDirector) || 0,
                      grossForNIAsDirector: Number(allValues?.grossForNIAsDirector) || 0,
                      eeNIAsDirector: Number(allValues?.eeNIAsDirector) || 0,
                      erNIAsDirector: Number(allValues?.erNIAsDirector) || 0,
                    }),
                  },
                },
                cisSettings: null,
              }),
        },
        // Step 7:
        pensionSetting: {
          category: allValues?.pensionCategory?.value || null,
          dateOnClassifiedOn:
            allValues?.dateOnClassifiedOn?.format('YYYY-MM-DD') || null,
          lastAssessmentOn:
            allValues?.lastAssessmentOn?.format('YYYY-MM-DD') || null,
          memberId: allValues?.membershipId || '',
          postponementEndDate:
            allValues?.postponementEndDate?.format('YYYY-MM-DD') || null,
          membershipStartDate:
            allValues?.membershipStartDate?.format('YYYY-MM-DD') || null,
          otpState: allValues?.otpState?.value || null,
          otpInDate: allValues?.otpInDate?.format('YYYY-MM-DD') || null,
          otpOutDate: allValues?.otpOutDate?.format('YYYY-MM-DD') || null,
        },
        // Step 8:
        attachmentOrder: {
          code: (allValues?.deductionDescription as any)?.code?.value || null,
          isActive: allValues?.attachmentOrderStatus?.value === 'ACTIVE',
          deductionDesc: allValues?.deductionDescription?.value || null,
          priority: Number(allValues?.attachmentOrderPriority) || 0,
          effectiveDate:
            allValues?.attachmentOrderEffectiveDate?.format('YYYY-MM-DD') ||
            null,
          endDate:
            allValues?.attachmentOrderEndDate?.format('YYYY-MM-DD') || null,
          deductionRate: Number(allValues?.deductionRate) || 0,
          protectedMin: Number(allValues?.protectedMinimum) || 0,
          protectedMinType: (allValues?.protectedMinType as 'percentage' | 'amount') ?? 'percentage',
          marginFee: Number(allValues?.marginFee) || 0,
          orderRef: allValues?.orderRef || '',
          totalAttachment: toTotalAttachmentPayload(allValues?.totalAttachment),
        },
      };
    } else if (isUK) {
      payload = {
        // Step 1:
        workerType: allValues?.employmentStatus || null,
        // Step 2:
        contractType: allValues?.contractType || null,
        // Step 3:
        contractDetail: allValues?.contractDetailType || null,
        dateOfJoining:
          allValues?.employmentStartDate?.format('YYYY-MM-DD') || null,
        // Step 4:
        title: allValues?.title?.value || null,
        forename: allValues?.forename || '',
        surname: allValues?.surname || '',
        gender: allValues?.gender?.value || null,
        DOB: allValues?.DOB?.format('YYYY-MM-DD') || null,
        email: allValues?.email || '',
        mobile: allValues?.phoneNumber || '',
        NINumber: allValues?.NINumber || '',
        address: [allValues?.address1 || '', allValues?.address2 || ''],
        city: allValues?.city || '',
        county:
          typeof allValues?.county === 'object'
            ? (allValues?.county as any)?.value || ''
            : allValues?.county || '',
        postCode: allValues?.postCode || '',
        country: allValues?.country?.value || null,
        // Step 6:
        department: allValues?.department?.value || '',
        employmentSetting: {
          companyId: allValues?.employer?.value || '',
          employmentStartDate:
            allValues?.employmentStartDate?.format('YYYY-MM-DD') || null,
          employmentBasis: allValues?.employmentBasis?.value || null,
          payFrequency: allValues?.payFrequency || null,
          paymentMethod: PAYMENT_MODEL_ENUM.PAYEE,
          ...(allValues?.paymentMethod?.value === PAYMENT_MODEL_ENUM.CIS ||
          allValues?.paymentMethod?.value === PAYMENT_MODEL_ENUM.NON_CIS
            ? {
                payeUmbrellaSettings: null,
                cisSettings: {
                  businessType: allValues?.businessType?.value || null,
                  UTRNo: allValues?.utrNumber || '',
                  VATRegNo: allValues?.vatRegNo || '',
                  domesticReverseChargeApplicable:
                    allValues?.domesticReverseChargeApplicable || false,
                  cisStatus: allValues?.cisStatus?.value || null,
                  personalVerificationNumber:
                    allValues?.personalVerificationNumber || '',
                  verificationDate:
                    allValues?.verificationDate?.format('YYYY-MM-DD') || null,
                  matchDate: allValues?.matchDate?.format('YYYY-MM-DD') || null,
                  companyName: allValues?.companyName || '',
                  registrationNumber: allValues?.registrationNumber || '',
                  firmName: allValues?.firmName || '',
                  partnersName: allValues?.partnersName || '',
                  partnersCategory: allValues?.partnersCategory?.value || null,
                },
              }
            : {
                payeUmbrellaSettings: {
                  taxCode: allValues?.taxCode || '',
                  NICategory: allValues?.NICategory?.value || null,
                  starterDeclaration:
                    allValues?.starterDeclaration?.value || null,
                  taxBasis: allValues?.taxBasis?.value || null,
                  NIDirectorStartDate:
                    allValues?.NIDirectorStartDate?.format('YYYY-MM-DD') ||
                    null,
                  NIDirectorMethod: allValues?.NIDirectorMethod?.value || null,
                  workplacePostcode: allValues?.workplacePostcode || '',
                  previousPayrollId: allValues?.previousPayrollId || '',
                  previousPayrollIdUnknown:
                    allValues?.previousPayrollIdUnknown ?? false,
                  salary: Number(allValues?.salary) || 0,
                  previousJobTaxInfo: {
                    p45GrossToDate: Number(allValues?.p45GrossToDate) || 0,
                    p45TaxToDate: Number(allValues?.p45TaxToDate) || 0,
                  },
                  openingBalance: {
                    eeNI: Number(allValues?.employeeNI) || 0,
                    erNI: Number(allValues?.employerNI) || 0,
                    eePension: Number(allValues?.employeePension) || 0,
                    erPension: Number(allValues?.employerPension) || 0,
                    stLoan: Number(allValues?.studentLoan) || 0,
                    pgLoan: Number(allValues?.postGraduateStudentLoan) || 0,
                    smp: Number(allValues?.smp) || 0,
                    spp: Number(allValues?.spp) || 0,
                    sap: Number(allValues?.sap) || 0,
                    ssp: Number(allValues?.ssp) || 0,
                    sppa: Number(allValues?.shpp) || 0,
                    sncp: Number(allValues?.sncp) || 0,
                    grossToLel: Number(allValues?.grossToLel) || 0,
                    grossLelToPt: Number(allValues?.grossLelToPt) || 0,
                    grossPtToUEL: Number(allValues?.grossPtToUEL) || 0,
                    gross: Number(allValues?.gross) || 0,
                    grossForTax: Number(allValues?.grossForTax) || 0,
                    grossForNI: Number(allValues?.grossForNI) || 0,
                    tax: Number(allValues?.tax) || 0,
                    // Director NI Contribution
                    ...(allValues?.employmentStatus === 'DIRECTOR' && {
                      grossToLelAsDirector: Number(allValues?.grossToLelAsDirector) || 0,
                      grossLelToPtAsDirector: Number(allValues?.grossLelToPtAsDirector) || 0,
                      grossPtToUELAsDirector: Number(allValues?.grossPtToUELAsDirector) || 0,
                      grossForNIAsDirector: Number(allValues?.grossForNIAsDirector) || 0,
                      eeNIAsDirector: Number(allValues?.eeNIAsDirector) || 0,
                      erNIAsDirector: Number(allValues?.erNIAsDirector) || 0,
                    }),
                  },
                },
                cisSettings: null,
              }),
        },
        // Step 7:
        pensionSetting: {
          category: allValues?.pensionCategory?.value || null,
          dateOnClassifiedOn:
            allValues?.dateOnClassifiedOn?.format('YYYY-MM-DD') || null,
          lastAssessmentOn:
            allValues?.lastAssessmentOn?.format('YYYY-MM-DD') || null,
          memberId: allValues?.membershipId || '',
          postponementEndDate:
            allValues?.postponementEndDate?.format('YYYY-MM-DD') || null,
          membershipStartDate:
            allValues?.membershipStartDate?.format('YYYY-MM-DD') || null,
          otpState: allValues?.otpState?.value || null,
          otpInDate: allValues?.otpInDate?.format('YYYY-MM-DD') || null,
          otpOutDate: allValues?.otpOutDate?.format('YYYY-MM-DD') || null,
        },
        // Step 8:
        attachmentOrder: {
          code: (allValues?.deductionDescription as any)?.code?.value || null,
          isActive: allValues?.attachmentOrderStatus?.value === 'ACTIVE',
          deductionDesc: allValues?.deductionDescription?.value || null,
          priority: Number(allValues?.attachmentOrderPriority) || 0,
          effectiveDate:
            allValues?.attachmentOrderEffectiveDate?.format('YYYY-MM-DD') ||
            null,
          endDate:
            allValues?.attachmentOrderEndDate?.format('YYYY-MM-DD') || null,
          deductionRate: Number(allValues?.deductionRate) || 0,
          protectedMin: Number(allValues?.protectedMinimum) || 0,
          protectedMinType: (allValues?.protectedMinType as 'percentage' | 'amount') ?? 'percentage',
          marginFee: Number(allValues?.marginFee) || 0,
          orderRef: allValues?.orderRef || '',
          totalAttachment: toTotalAttachmentPayload(allValues?.totalAttachment),
        },
      };
    } else if (isIreland) {
      payload = {
        // Step 1:
        workerType: allValues?.employmentStatus || null,
        // Step 2:
        contractType: allValues?.contractType || null,
        // Step 3:
        contractDetail: allValues?.contractDetailType || null,
        dateOfJoining:
          allValues?.employmentStartDate?.format('YYYY-MM-DD') || null,
        // Step 4:
        title: allValues?.title?.value || null,
        forename: allValues?.forename || '',
        surname: allValues?.surname || '',
        gender: allValues?.gender?.value || null,
        DOB: allValues?.DOB?.format('YYYY-MM-DD') || null,
        email: allValues?.email || '',
        mobile: allValues?.phoneNumber || '',
        NINumber: allValues?.NINumber || '',
        address: [allValues?.address1 || '', allValues?.address2 || ''],
        city: allValues?.city || '',
        county:
          typeof allValues?.county === 'object'
            ? (allValues?.county as any)?.value || ''
            : allValues?.county || '',
        postCode: allValues?.postCode || '',
        country: allValues?.country?.value || null,
        // Step 6:
        department: allValues?.department?.value || '',
        employmentSetting: {
          companyId: allValues?.employer?.value || '',
          employmentStartDate:
            allValues?.employmentStartDate?.format('YYYY-MM-DD') || null,
          employmentBasis: allValues?.employmentBasis?.value || null,
          payFrequency: allValues?.payFrequency || null,
          paymentMethod: allValues?.paymentMethod?.value || null,
          ...((allValues?.paymentMethod?.value === PAYMENT_MODEL_ENUM.CIS ||
          allValues?.paymentMethod?.value === PAYMENT_MODEL_ENUM.NON_CIS)
            ? {
                irelandPayeSettings: null as any,
                cisSettings: {
                  cisStatus: allValues?.cisStatus?.value || null,
                  subcontractorRefNumber:
                    allValues?.subcontractorRefNumber || '',
                  businessType: 'SOLE_TRADER',
                  UTRNo: null,
                  VATRegNo: null,
                  domesticReverseChargeApplicable: null,
                  personalVerificationNumber: null,
                  verificationDate: null,
                  matchDate: null,
                  companyName: null,
                  registrationNumber: null,
                  firmName: null,
                  partnersName: null,
                  partnersCategory: null,
                },
              }
            : {
                irelandPayeSettings: {
                  taxBasis: allValues?.taxBasis?.value || null,
                  salary: Number(allValues?.salary) || 0,
                  ppsNumber: allValues?.ppsNumber || '',
                  employmentStatus:
                    allValues?.employmentStatusIreland?.value || null,
                  employmentId: allValues?.employmentId || '',
                  prsiEeClasses: allValues?.prsiEeClasses?.value || null,
                  prsiExemption: allValues?.prsiExemption?.value || null,
                  directorShip: allValues?.directorShip?.value || null,
                  NIDirectorMethod:
                    allValues?.employmentStatus === 'DIRECTOR'
                      ? 'ALTERNATIVE'
                      : null,
                  uscExemption: allValues?.uscExemption?.value || null,
                  taxCreditAnnual: Number(allValues?.taxCreditAnnual) || 0,
                  taxCreditFrequency: allValues?.taxCreditFrequency || null,
                  taxRateCutOffPoints: allValues?.taxRateCutOffPoints
                    ? [
                        {
                          index: 1,
                          taxRatePercent: 20,
                          yearlyRateCutOff:
                            Number(allValues?.taxRateCutOffPoints) || 0,
                        },
                        {
                          index: 2,
                          taxRatePercent: 40,
                        },
                      ]
                    : null,
                  yearlyRateCutOff: allValues?.taxRateCutOffPoints,
                  yearlyRateCutOffFrequency:
                    allValues?.taxRateCutOffPointsFrequency,
                  cutOffPoints: allValues?.cutOffPointsType
                    ? {
                        type: allValues?.cutOffPointsType?.value || null,
                        values: [
                          {
                            index: 1,
                            uscRatePercent: 0.5,
                            yearlyUSCRateCutOff:
                              Number(allValues?.cutOffPointsZeroPointFive) || 0,
                          },
                          {
                            index: 2,
                            uscRatePercent: 2,
                            yearlyUSCRateCutOff:
                              Number(allValues?.cutOffPointsTwo) || 0,
                          },
                          {
                            index: 3,
                            uscRatePercent: 3,
                            yearlyUSCRateCutOff:
                              Number(allValues?.cutOffPointsFourPointFive) || 0,
                          },
                          {
                            index: 4,
                            uscRatePercent: 8,
                          },
                        ],
                      }
                    : null,
                  totalLPTToBeDeducted:
                    Number(allValues?.totalLPTToBeDeducted) || 0,
                  ascCalculationMethod:
                    allValues?.ascCalculationMethod?.value || null,
                },
                cisSettings: null,
              }),
        },
        // Step 7:
        attachmentOrder: {
          code: (allValues?.deductionDescription as any)?.code?.value || null,
          isActive: allValues?.attachmentOrderStatus?.value === 'ACTIVE',
          deductionDesc: allValues?.deductionDescription?.value || null,
          priority: Number(allValues?.attachmentOrderPriority) || 0,
          effectiveDate:
            allValues?.attachmentOrderEffectiveDate?.format('YYYY-MM-DD') ||
            null,
          endDate:
            allValues?.attachmentOrderEndDate?.format('YYYY-MM-DD') || null,
          deductionRate: Number(allValues?.deductionRate) || 0,
          protectedMin: Number(allValues?.protectedMinimum) || 0,
          protectedMinType: (allValues?.protectedMinType as 'percentage' | 'amount') ?? 'percentage',
          marginFee: Number(allValues?.marginFee) || 0,
          orderRef: allValues?.orderRef || '',
          totalAttachment: toTotalAttachmentPayload(allValues?.totalAttachment),
        },
      };
    }

    // NX-4948: salary pro-rata inputs, same keys for every country/product.
    payload = { ...payload, ...buildWorkingPatternCreateParams(allValues) };

    const response = await createEmployee(payload);
    if (response?.success) {
      const createdEmployeeId = response?.data?.id;
      if (createdEmployeeId) {
        try {
          await updateBank(createdEmployeeId, {
            bankName: allValues?.bankName?.value || '',
            accountName: allValues?.accountName || '',
            accountNumber: allValues?.accountNumber || '',
            sortCode: allValues?.sortCode || '',
            buildingSocietyNumber: allValues?.buildingSocietyNumber || '',
            IBANReference: allValues?.IBANReference || '',
            SWIFTNo: allValues?.SWIFTNo || '',
            country: allValues?.bankCountry?.value || null,
            isInternalAccount: allValues?.isInternationalAccount ?? false,
          } as any);
        } catch (error) {
          // The employee already exists and useUpdateBankByEmployeeId has
          // surfaced the error; carry on closing the modal so a retry cannot
          // create a duplicate employee.
          console.error(error);
        }
      }

      showMessage({
        type: 'success',
        content: messages['employee.create.success'],
      });

      //Reset Fields
      employmentStatusFormRef.current?.resetFields();
      contractTypeFormRef.current?.resetFields();
      contractDetailFormRef.current?.resetFields();
      personalDetailFormRef.current?.resetFields();
      bankDetailFormRef.current?.resetFields();
      employmentFormRef.current?.resetFields();
      pensionFormRef.current?.resetFields();
      attachmentOrderFormRef.current?.resetFields();

      toggle();
      refetchList();
      setFormValues(null);
      setCurrentStepIndex(0);
    }
  };

  return (
    <AppModalContainer
      title={messages['common.addEmployee']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        ...(activeStepIndex > 0
          ? [
              {
                name: messages['common.back'],
                variant: 'outlined',
                onClick: handleBack,
              },
            ]
          : ([] as any)),
        ...(activeStepIndex === steps.length - 1
          ? [
              {
                name: messages['common.createEmployee'],
                leftIcon: (
                  <Icons.IconCheckCircle
                    color={defaultTheme.theme.palette.white}
                  />
                ),
                onClick: handleSubmit,
                loading: createEmployeeLoading || updateBankLoading,
                disabled: createEmployeeLoading || updateBankLoading,
              },
            ]
          : [
              {
                name: messages['common.saveAndContinue'],
                buttonType: 'primary',
                rightIcon: (
                  <Icons.IconArrowRight
                    color={defaultTheme.theme.palette.white}
                  />
                ),
                onClick: handleSubmitToContinue,
              },
            ]),
      ]}
      width={896}
    >
      <div className="flex gap-x-[64px]">
        <div className="w-[264px] space-y-[16px]">{steps.map(renderStep)}</div>
        <div className="flex-1">
          {renderFormByStep(steps[activeStepIndex])}
        </div>
      </div>
    </AppModalContainer>
  );
};

export default CreateEmployeeModal;
